// ============================================================================
// Export — die drei Helfer von webgis VERBATIM plus die Sofortwege (SPEC §5.6.10).
//
// `slugify`, `dataUrlToBlob` und `download` sind wörtlich aus
// /opt/webgis/frontend/src/components/ChartExportModal.tsx übernommen
// (design.json → "Export — download helper + PDF via jspdf/svg2pdf"); nur der
// Rückfallname ist "zeitplan" statt "diagramm". `download(blob, name)` ist der
// Vertrag, auf den auch `TopNav` (fe-shell) baut: ZUERST der Blob, DANN der
// Name.
//
// `window.claude.use("downloads")` aus dem Prototyp ist hier ABSICHTLICH nicht
// portiert — das ist die Artifact-Laufzeit und auf zeitplan.lilasp.de toter
// Code, den ein try/catch nur verstecken würde (§5.6.10).
//
// ---------------------------------------------------------------------------
// DER SOFORTWEG ZEICHNET AUS DER SZENE, NICHT AUS DEM DOM. Der einzige
// Ausnahmefall ist der PDF-Rückfall, weil `svg2pdf` am LEBENDEN Knoten misst
// (§5.6.10) — und dort werden die Editor-Knoten (`[data-ed]`) vorher entfernt,
// sonst wandern Hilfslinien, Auswahlrahmen und Griffe mit ins PDF.
// Alles andere geht über `buildScene(..., { editor: false })` und
// `sceneToSvgString` — dieselben zwei Funktionen, die der Server benutzt
// (T8, §6.1). Damit gibt es keinen zweiten Zeichner.
// ============================================================================
import { buildScene, KOPF_H, PAPER, sceneToSvgString } from "@kern";
import type { FerienQuelle, Kopfzeile, Messer, Plan } from "@kern";
import type { Seitenformat } from "../api/typen";
import type { TtfSchnitt } from "./poppins-ttf";

// ---------------------------------------------------------------- Helfer

/** Plantitel → Dateiname: Umlaute ausgeschrieben, Rest auf a-z0-9-. */
export function slugify(name: string): string {
  const map: Record<string, string> = { ä: "ae", ö: "oe", ü: "ue", ß: "ss" };
  return (
    name
      .toLowerCase()
      .replace(/[äöüß]/g, (c) => map[c] ?? c)
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "zeitplan"
  );
}

/** Daten-URL → Blob (`atob` liefert eine Latin-1-Zeichenkette, kein UTF-8). */
export function dataUrlToBlob(url: string, mime: string): Blob {
  const bin = atob(url.slice(url.indexOf(",") + 1));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return new Blob([out], { type: mime });
}

/**
 * Blob als Datei anbieten — `<a download>` mit kurzlebiger Blob-URL.
 * Reihenfolge der Argumente: (blob, dateiname). So ruft es TopNav (§5.8.2).
 */
export function download(blob: Blob, dateiname: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ------------------------------------------------------------- Schriften

/**
 * Die drei Schriftdateien des Blatts — dieselben Pfade wie in `base.css`
 * (§2.7: der Name trägt die ersten acht Zeichen des Inhalts-sha256).
 * Familienname `Poppins`, in `CHART_FAM`, `base.css` und hier derselbe —
 * sonst setzt das exportierte SVG in der Ersatzschrift (Anhang A.10).
 */
export const BLATT_SCHRIFTEN: readonly { datei: string; weight: 400 | 500; italic: boolean }[] = [
  { datei: "/fonts/poppins-latin-400-7d93459d.woff2", weight: 400, italic: false },
  { datei: "/fonts/poppins-latin-500-cd36de20.woff2", weight: 500, italic: false },
  { datei: "/fonts/poppins-latin-i400-50d0c174.woff2", weight: 400, italic: true },
];

/** Eine Datei als `data:`-URI mit FESTEM Medientyp. */
async function alsDataUri(pfad: string): Promise<string> {
  const antwort = await fetch(pfad, { cache: "force-cache" });
  if (!antwort.ok) throw new Error("Schrift nicht ladbar: " + pfad);
  const blob = await antwort.blob();
  const roh = await new Promise<string>((erfuellt, gescheitert) => {
    const leser = new FileReader();
    leser.onload = () => erfuellt(String(leser.result));
    leser.onerror = () => gescheitert(new Error("Schrift nicht lesbar: " + pfad));
    leser.readAsDataURL(blob);
  });
  // Der Medientyp wird NEU gesetzt und nicht vom Server übernommen: liefert
  // nginx die Datei als application/octet-stream, wäre die Regel mit
  // `format('woff2')` uneinheitlich, und Safari lehnt sie ab.
  return "data:font/woff2;base64," + roh.slice(roh.indexOf(",") + 1);
}

/** Einmal geholt, dann im Modul gehalten (§5.6.10). */
let schriftCss: Promise<string> | null = null;

/**
 * Die drei `@font-face`-Regeln mit `data:font/woff2;base64,…` — für das SVG
 * und das PNG des Sofortwegs.
 *
 * Es ist eine FUNKTION und keine Konstante, obwohl Anhang A.9 nur den Namen
 * führt: die drei Dateien müssen erst über `fetch("/fonts/…")` geholt werden,
 * und das geht in einem Modulkopf nicht. Beim ersten Aufruf geholt, danach im
 * Modul gehalten (§5.6.10). Aufrufstellen schreiben `await FONT_CSS_EXPORT()`.
 *
 * GRUND für das Einbetten: ein `<img>`, das eine `data:`-SVG lädt, ist ein
 * eigenes Dokument und kommt an `/fonts/` nicht heran.
 *
 * Scheitert das Holen, WIRFT diese Funktion — sie liefert keine leere
 * Zeichenkette. Ein Bild in der Ersatzschrift bricht anders um als das Blatt
 * auf dem Schirm (R1, R-24); lieber eine Fehlermeldung mit dem Angebot, über
 * den Server zu gehen, als ein stilles falsches Erzeugnis. Beim nächsten
 * Versuch wird neu geholt.
 */
export function FONT_CSS_EXPORT(): Promise<string> {
  if (!schriftCss) {
    schriftCss = Promise.all(
      BLATT_SCHRIFTEN.map(async (s) => {
        const uri = await alsDataUri(s.datei);
        return (
          "@font-face{font-family:'Poppins';font-style:" +
          (s.italic ? "italic" : "normal") +
          ";font-weight:" +
          s.weight +
          ";font-display:block;src:url(" +
          uri +
          ") format('woff2')}"
        );
      }),
    ).then(
      (teile) => teile.join("\n"),
      (e: unknown) => {
        schriftCss = null;
        throw e instanceof Error ? e : new Error("Schriften nicht ladbar");
      },
    );
  }
  return schriftCss;
}

// ----------------------------------------------------------- Sofortwege

/** Was jeder Sofortweg braucht, um das Blatt ohne Server zu zeichnen. */
export interface SofortOpt {
  plan: Plan;
  messer: Messer;
  ferien?: FerienQuelle | null;
  /** Exportbeiwerk über dem Blatt, `null` = ohne (§5.6.10). */
  kopfzeile?: Kopfzeile | null;
  /** false = transparent statt Papier (nur PNG und SVG). Vorgabe true. */
  hintergrund?: boolean;
}

/** Das Erzeugnis eines Sofortwegs samt der Maße, die das PNG braucht. */
interface Blattbild {
  svg: string;
  breite: number;
  hoehe: number;
}

/**
 * Szene → SVG-Dokument. IMMER `editor: false` — ein Export trägt NIE Knoten
 * mit `ed: true` (§3.9).
 */
async function bauen(o: SofortOpt): Promise<Blattbild> {
  const fontCss = await FONT_CSS_EXPORT();
  const kopfzeile = o.kopfzeile ?? null;
  const szene = buildScene(o.plan, o.messer, {
    editor: false,
    ferien: o.ferien ?? null,
    hintergrund: o.hintergrund !== false,
  });
  return {
    // `hintergrund` geht MIT: der Papier-Knoten der Szene deckt erst ab KOPF_H,
    // das Kopfband bekommt seinen Grund vom SVG-Erbauer (R1-7). Ohne die
    // Weitergabe trüge auch ein transparentes Blatt oben ein cremefarbenes
    // Band.
    svg: sceneToSvgString(szene, { fontCss, kopfzeile, hintergrund: o.hintergrund !== false }),
    breite: szene.breite,
    // Die Kopfzeile schiebt das Blatt um KOPF_H nach unten und erhöht die
    // Höhe um genau diesen Betrag (§3.10) — das Bild muss mitwachsen, sonst
    // schneidet das PNG unten ab.
    hoehe: szene.hoehe + (kopfzeile ? KOPF_H : 0),
  };
}

/**
 * `sceneToSvgString(buildScene(plan, messer, {editor:false, …}), {fontCss, kopfzeile})`.
 * Immer `editor: false` — ein Export trägt NIE Knoten mit `ed: true` (§3.9).
 */
export async function svgZeichenkette(o: SofortOpt): Promise<string> {
  return (await bauen(o)).svg;
}

/** SVG sofort: Zeichenkette → Blob → `download(blob, basis + ".svg")`. */
export async function exportSvg(o: SofortOpt, basis: string): Promise<void> {
  const s = await svgZeichenkette(o);
  download(new Blob([s], { type: "image/svg+xml" }), basis + ".svg");
}

/**
 * Höchste Auflösung, die der Sofortweg im Browser zeichnet. Ab 600 dpi geht es
 * über den Server, weil ein Canvas dieser Größe auf dem Telefon nicht mehr
 * zuverlässig `toBlob` liefert (§5.6.10).
 */
export const PNG_SOFORT_MAX_DPI = 288;

/**
 * PNG sofort: dieselbe Rechnung wie der Serverweg, `pixel = w · dpi/96`
 * (§6.3/R-16) — erst `PAPER` als Grund füllen, dann `drawImage` in Zielgröße.
 * Chrome rastert das SVG in ZIELauflösung; das Ergebnis ist scharf, weil das
 * Bild nie erst in Blattgröße entsteht.
 */
export async function exportPng(o: SofortOpt & { dpi: number }, basis: string): Promise<void> {
  const bild = await bauen(o);
  const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(bild.svg);
  const img = new Image();
  await new Promise<void>((erfuellt, gescheitert) => {
    img.onload = () => erfuellt();
    img.onerror = () => gescheitert(new Error("Das Blatt ließ sich nicht in ein Bild zeichnen."));
    img.src = url;
  });

  const faktor = Math.max(1, o.dpi) / 96;
  const cv = document.createElement("canvas");
  cv.width = Math.round(bild.breite * faktor);
  cv.height = Math.round(bild.hoehe * faktor);
  const cx = cv.getContext("2d");
  if (!cx) throw new Error("Dieser Browser gibt keinen Zeichenbereich her.");
  // Nur mit Papiergrund füllen: bei `hintergrund: false` lässt der Kern das
  // Papier-Rechteck weg (I8), und ein gefüllter Canvas machte die Wahl
  // wirkungslos.
  if (o.hintergrund !== false) {
    cx.fillStyle = PAPER;
    cx.fillRect(0, 0, cv.width, cv.height);
  }
  cx.drawImage(img, 0, 0, cv.width, cv.height);

  const blob = await new Promise<Blob | null>((r) => cv.toBlob(r, "image/png"));
  if (!blob) throw new Error("Der Browser hat kein Bild geliefert.");
  download(blob, basis + ".png");
}

/** JSON: der Plan, wie er ist. Der einzige Weg, den der Server nicht kennt (I5). */
export function exportJson(plan: Plan, basis: string): void {
  download(new Blob([JSON.stringify(plan, null, 1)], { type: "application/json" }), basis + ".json");
}

// ------------------------------------------------------------------ PDF

/** Seitenmaße in mm, quer. `frei` rechnet aus der Blattbreite (T25). */
const SEITE_MM: Record<"a3" | "a4", readonly [number, number]> = {
  a3: [420, 297],
  a4: [297, 210],
};

/** px → mm bei 96 dpi. Dieselbe Rechnung wie im Serverweg (§6.3). */
const mm = (px: number) => (px * 25.4) / 96;

/**
 * Poppins in der PDF-Schrifttabelle anmelden — alle drei Schnitte (T46).
 *
 * `svg2pdf` setzt jede Familie, die jsPDF nicht kennt, in TIMES. Genau das war
 * das „komische“ PDF, sobald der Serverweg ausfiel: früher wurde hier eine
 * `/fonts/poppins-latin-400.ttf` versucht, die es nie gab, und der Fehlschlag
 * war still. Jetzt kommen die TrueType-Fassungen als Modul in das ohnehin faul
 * geladene PDF-Bündel (`poppins-ttf.ts`, erzeugt aus denselben woff2 wie im
 * Backend-Abbild) — keine zweite Datei unter `/fonts/`, also keine Cachefalle.
 *
 * Gewicht und Stil so, wie `svg2pdf` danach fragt: 400 → "normal", kursiv →
 * "italic", 500 → "500normal". Wer hier ein Gewicht vergisst, bekommt genau für
 * diese Zeilen wieder Times.
 */
function poppinsAnmelden(
  doc: {
    addFileToVFS(name: string, daten: string): void;
    addFont(datei: string, familie: string, stil: string, gewicht?: number | string): string;
  },
  schnitte: readonly TtfSchnitt[],
): void {
  for (const s of schnitte) {
    doc.addFileToVFS(s.datei, s.daten);
    doc.addFont(s.datei, "Poppins", s.stil, s.gewicht);
  }
}

/**
 * PDF als RÜCKFALL, wenn der Serverweg nicht antwortet.
 *
 * `svg2pdf` MISST AM LEBENDEN KNOTEN: der Knoten muss im Dokument hängen,
 * solange es läuft (Wirt bei `left:-100000px`, im `finally` entfernen).
 * `jspdf` und `svg2pdf.js` kommen NUR über
 * `await Promise.all([import("jspdf"), import("svg2pdf.js")])` herein —
 * sie wiegen mehr als der Rest der Anwendung (Bündel `pdf`, §5.1.3).
 *
 * Gezeichnet wird ein Klon des LEBENDEN `<svg>` (so steht es in §5.6.10),
 * aber OHNE die Knoten mit `data-ed` — sonst stünden Hilfslinien, Auswahl-
 * rahmen und Griffe im PDF. `Blatt.tsx` setzt dieses Attribut auf jeden
 * Knoten mit `ed: true`.
 */
export async function exportPdfSofort(
  o: { svg: SVGSVGElement; breite: number; hoehe: number; format: Seitenformat },
  basis: string,
): Promise<void> {
  const [{ jsPDF }, { svg2pdf }, { POPPINS_TTF }] = await Promise.all([
    import("jspdf"),
    import("svg2pdf.js"),
    import("./poppins-ttf"),
  ]);

  const bmm = mm(o.breite);
  const hmm = mm(o.hoehe);
  const seite: readonly [number, number] =
    o.format === "a3" || o.format === "a4" ? SEITE_MM[o.format] : [bmm, hmm];

  const doc = new jsPDF({
    orientation: seite[0] >= seite[1] ? "landscape" : "portrait",
    unit: "mm",
    format: [seite[0], seite[1]],
  });
  poppinsAnmelden(doc, POPPINS_TTF);

  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  // Einpassen wie in webgis: das Blatt wird nie beschnitten und nie verzerrt.
  const skala = Math.min(pw / bmm, ph / hmm);
  const zielB = bmm * skala;
  const zielH = hmm * skala;

  const wirt = document.createElement("div");
  wirt.setAttribute("aria-hidden", "true");
  wirt.style.cssText = "position:fixed;left:-100000px;top:0;pointer-events:none;";
  const knoten = o.svg.cloneNode(true) as SVGSVGElement;
  knoten.removeAttribute("id");
  knoten.querySelectorAll("[data-ed]").forEach((n) => n.remove());
  knoten.setAttribute("width", String(o.breite));
  knoten.setAttribute("height", String(o.hoehe));
  knoten.style.width = o.breite + "px";
  knoten.style.height = o.hoehe + "px";
  wirt.appendChild(knoten);
  document.body.appendChild(wirt);

  try {
    await svg2pdf(knoten, doc, { x: (pw - zielB) / 2, y: (ph - zielH) / 2, width: zielB, height: zielH });
    download(doc.output("blob"), basis + ".pdf");
  } finally {
    wirt.remove();
  }
}
